import { lazy, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { PermissionNames } from "~enums/permissions.ts";
import { SeasonTabKeys } from "~enums/seasonTabKeys.ts";
import usePermissionsByCompetitionName from "~hooks/usePermissionsByCompetitionName";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import type { TabRoute } from "../types/TabRoute.ts";

const SeasonPoolListPage = lazy(() => import("~pages/SeasonPools/SeasonPoolListPage/SeasonPoolListPage"));
const SeasonEditPage = lazy(() => import("~Features/EditSeason/EditSeason"));
const SeasonStageListPage = lazy(() => import("~pages/SeasonStages/SeasonStageListPage/SeasonStageListPage"));
const SeasonEntityListPage = lazy(() => import("~pages/SeasonEntities/SeasonEntityListPage/SeasonEntityListPage"));
const SeasonSeriesListPage = lazy(() => import("~Features/SeriesTab/SeriesTab"));
const SeasonVenueListPage = lazy(() => import("~pages/Venues/SeasonVenueListPage/SeasonVenueListPage"));
const SeasonEntityImagesPage = lazy(() => import("~Features/SeasonTeamsImages/SeasonTeamImages"));
const SeasonStandingsPage = lazy(() => import("~pages/Standings/StandingsListPage/StandingsListPage"));
const SeasonFinalStandingsPage = lazy(() => import("~pages/FinalStandings/FinalStandingsPage"));
const SeasonEntityStatsPage = lazy(() => import("~Features/SeasonTeamStats/SeasonTeamStats"));
const FixturesListPage = lazy(() => import("~pages/Fixtures/FixtureListPage"));
const SeasonReportsPage = lazy(() => import("~pages/Reports/SeasonReportsPage"));
const SeasonOfficialsListPage = lazy(() => import("~pages/Officials/SeasonOfficialsListPage/SeasonOfficialsListPage"));
const AssignFixtureOfficialsPage = lazy(() => import("~pages/Officials/AssignFixtureOfficialsPage"));

const useGetSeasonTabRoutes = (): TabRoute<SeasonTabKeys>[] => {
  const { seasonEntityPermissionName, fixturePermissionName } = usePermissionsByCompetitionName();
  const { uiConfiguration } = useSportConfiguration();
  const { seasonTabs } = uiConfiguration;

  return useMemo(() => {
    const routes: TabRoute<SeasonTabKeys>[] = [
      {
        key: SeasonTabKeys.Fixtures,
        path: "fixtures",
        label: <FormattedMessage id="season.tabs.fixtures" />,
        Component: FixturesListPage,
        permissionKeys: [fixturePermissionName],
      },
      {
        key: SeasonTabKeys.Stages,
        path: "stages",
        label: <FormattedMessage id="season.tabs.stages" />,
        Component: SeasonStageListPage,
        permissionKeys: [PermissionNames.STAGE],
      },
      {
        key: SeasonTabKeys.Pools,
        path: "pools",
        label: <FormattedMessage id="season.tabs.pools" />,
        Component: SeasonPoolListPage,
        permissionKeys: [PermissionNames.POOL],
      },
      {
        key: SeasonTabKeys.Series,
        path: "series",
        label: <FormattedMessage id="season.tabs.series" />,
        Component: SeasonSeriesListPage,
        permissionKeys: [PermissionNames.SEASON_SERIES],
      },
      {
        key: SeasonTabKeys.Entities,
        path: "entities",
        label: <FormattedMessage id="season.tabs.entities" />,
        Component: SeasonEntityListPage,
        permissionKeys: [seasonEntityPermissionName],
      },
      {
        key: SeasonTabKeys.EntityImages,
        path: "entity-images",
        label: <FormattedMessage id="season.tabs.entity.images" />,
        Component: SeasonEntityImagesPage,
        permissionKeys: [seasonEntityPermissionName],
      },
      {
        key: SeasonTabKeys.EntityStatistic,
        path: "entity-statistic",
        label: <FormattedMessage id="season.tabs.entity.stats" />,
        Component: SeasonEntityStatsPage,
        permissionKeys: [PermissionNames.SEASON_STATISTIC],
      },
      {
        key: SeasonTabKeys.Officials,
        path: "officials",
        label: <FormattedMessage id="season.tabs.seasonOfficials" />,
        Component: SeasonOfficialsListPage,
        permissionKeys: [PermissionNames.ROLES],
      },
      {
        key: SeasonTabKeys.AssignOfficials,
        path: "assign-officials",
        label: <FormattedMessage id="season.tabs.assignOfficials" />,
        Component: AssignFixtureOfficialsPage,
        permissionKeys: [PermissionNames.ROLES],
      },
      {
        key: SeasonTabKeys.Venues,
        path: "venues",
        label: <FormattedMessage id="season.tabs.venues" />,
        Component: SeasonVenueListPage,
        permissionKeys: [PermissionNames.VENUE],
      },
      {
        key: SeasonTabKeys.Standings,
        path: "standings",
        label: <FormattedMessage id="season.tabs.standings" />,
        Component: SeasonStandingsPage,
        permissionKeys: [PermissionNames.STANDING],
      },
      {
        key: SeasonTabKeys.FinalStandings,
        path: "final-standings",
        label: <FormattedMessage id="season.tabs.finalStandings" />,
        Component: SeasonFinalStandingsPage,
        permissionKeys: [PermissionNames.STANDING],
      },
      {
        key: SeasonTabKeys.Reports,
        path: "reports",
        label: <FormattedMessage id="season.tabs.reports" />,
        Component: SeasonReportsPage,
        permissionKeys: [],
      },
      {
        key: SeasonTabKeys.Details,
        path: "details",
        label: <FormattedMessage id="season.tabs.details" />,
        Component: SeasonEditPage,
        permissionKeys: [PermissionNames.SEASON],
      },
    ];

    return routes.filter(({ key }) => seasonTabs[key].enabled);
  }, [fixturePermissionName, seasonEntityPermissionName, seasonTabs]);
};

export default useGetSeasonTabRoutes;
